// /app/frontend/src/pages/compliance/vendor/DueDiligenceList.jsx
// Due Diligence Assessments List Page

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { dueDiligenceAPI, entitiesAPI, vendorSimplifiedAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Plus, Search, Edit, Trash2, ClipboardCheck, AlertTriangle,
  CheckCircle, XCircle, Clock, Loader2, Building2, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';

// Status and outcome options
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'completed', label: 'Completed' },
];

const OUTCOME_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'acceptable', label: 'Acceptable' },
  { value: 'requires_action', label: 'Requires Action' },
  { value: 'unacceptable', label: 'Unacceptable' },
];

export default function DueDiligenceList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vendorIdFromUrl = searchParams.get('vendor');
  const { isDPO, isComplianceReadOnly: isReadOnly } = useAuth();

  const [assessments, setAssessments] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntity, setSelectedEntity] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedOutcome, setSelectedOutcome] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(vendorIdFromUrl || '');
  const [vendorName, setVendorName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);

  // Fetch data
  useEffect(() => {
    fetchData();
  }, [selectedEntity, selectedVendor]);

  const fetchData = async () => {
    try {
      setLoading(true);

      if (isDPO) {
        const entitiesRes = await entitiesAPI.getAll();
        setEntities(entitiesRes.data);
      }

      // Fetch vendor name if filtering by vendor
      if (selectedVendor) {
        try {
          const vendorRes = await vendorSimplifiedAPI.getOne(selectedVendor);
          setVendorName(vendorRes.data.vendor_name);
        } catch {
          setVendorName('');
        }
      } else {
        setVendorName('');
      }

      // Fetch assessments (filter by vendor if specified)
      const assessmentsRes = await dueDiligenceAPI.getAll(selectedEntity || undefined, { vendorId: selectedVendor || undefined });
      setAssessments(assessmentsRes.data);

      // Fetch dashboard stats
      const statsRes = await dueDiligenceAPI.getDashboard(selectedEntity || undefined);
      setDashboardStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!assessmentToDelete) return;
    try {
      await dueDiligenceAPI.delete(assessmentToDelete.id);
      toast.success('Assessment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete assessment');
    } finally {
      setDeleteDialogOpen(false);
      setAssessmentToDelete(null);
    }
  };

  // Filter assessments
  const filteredAssessments = assessments.filter(a => {
    const matchesSearch = !searchTerm || 
      a.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.assessment_reference?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !selectedStatus || a.status === selectedStatus;
    const matchesOutcome = !selectedOutcome || a.assessment_outcome === selectedOutcome;
    return matchesSearch && matchesStatus && matchesOutcome;
  });

  const getStatusBadge = (status) => {
    const colors = {
      'draft': 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      'in_progress': 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
      'under_review': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
      'completed': 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    };
    return (
      <Badge className={cn('font-medium', colors[status] || colors['draft'])}>
        {STATUS_OPTIONS.find(s => s.value === status)?.label || status}
      </Badge>
    );
  };

  const getOutcomeBadge = (outcome) => {
    const colors = {
      'pending': 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      'acceptable': 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
      'requires_action': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
      'unacceptable': 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    };
    const icons = {
      'pending': Clock,
      'acceptable': CheckCircle,
      'requires_action': AlertTriangle,
      'unacceptable': XCircle,
    };
    const Icon = icons[outcome] || Clock;
    return (
      <Badge className={cn('font-medium flex items-center gap-1', colors[outcome] || colors['pending'])}>
        <Icon className="h-3 w-3" />
        {OUTCOME_OPTIONS.find(o => o.value === outcome)?.label || outcome}
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="due-diligence-list-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Due Diligence Assessments</h1>
            <p className="text-muted-foreground">
              {vendorName ? (
                <>
                  Showing assessments for: <strong>{vendorName}</strong>
                  <Button 
                    variant="link" 
                    className="h-auto p-0 ml-2 text-sm"
                    onClick={() => {
                      setSelectedVendor('');
                      navigate('/compliance/due-diligence');
                    }}
                  >
                    (Show All)
                  </Button>
                </>
              ) : (
                'Third-party vendor due diligence tracking'
              )}
            </p>
          </div>
          {!isReadOnly && (
            <Button
              onClick={() => navigate(selectedVendor ? `/compliance/due-diligence/new?vendor=${selectedVendor}` : '/compliance/due-diligence/new')}
              data-testid="new-assessment-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Assessment
            </Button>
          )}
        </div>

        {/* Dashboard Summary Cards */}
        {dashboardStats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <ClipboardCheck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{dashboardStats.summary.total_assessments}</p>
                    <p className="text-xs text-muted-foreground">Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{dashboardStats.summary.acceptable}</p>
                    <p className="text-xs text-muted-foreground">Acceptable</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{dashboardStats.summary.requires_action}</p>
                    <p className="text-xs text-muted-foreground">Requires Action</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                    <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{dashboardStats.summary.unacceptable}</p>
                    <p className="text-xs text-muted-foreground">Unacceptable</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                    <Building2 className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{dashboardStats.summary.vendors_without_dd}</p>
                    <p className="text-xs text-muted-foreground">No Assessment</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Alerts Section */}
        {dashboardStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Vendors Without DD */}
            {dashboardStats.alerts.vendors_without_dd?.length > 0 && (
              <Card className="border-orange-200 dark:border-orange-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-orange-600" />
                    Vendors Without Assessment ({dashboardStats.summary.vendors_without_dd})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {dashboardStats.alerts.vendors_without_dd?.slice(0, 5).map(v => (
                    <div
                      key={v.id}
                      className={`flex items-center justify-between p-2 bg-orange-50 dark:bg-orange-950 rounded ${isReadOnly ? '' : 'cursor-pointer hover:bg-orange-100 dark:hover:bg-orange-900'}`}
                      onClick={() => !isReadOnly && navigate(`/compliance/due-diligence/new?vendor=${v.id}`)}
                    >
                      <span className="text-sm font-medium truncate">{v.vendor_name}</span>
                      {!isReadOnly && (
                        <Button variant="ghost" size="sm">
                          <Plus className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Requiring Action */}
            {dashboardStats.alerts.requiring_action?.length > 0 && (
              <Card className="border-yellow-200 dark:border-yellow-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    Assessments Requiring Action
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {dashboardStats.alerts.requiring_action?.slice(0, 5).map(a => (
                    <div 
                      key={a.id} 
                      className="flex items-center justify-between p-2 bg-yellow-50 dark:bg-yellow-950 rounded cursor-pointer hover:bg-yellow-100 dark:hover:bg-yellow-900"
                      onClick={() => navigate(`/compliance/due-diligence/${a.id}`)}
                    >
                      <span className="text-sm font-medium truncate">{a.assessment_reference}</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by vendor or reference..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>

              {isDPO && (
                <Select value={selectedEntity || "all"} onValueChange={(v) => setSelectedEntity(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All Entities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Entities</SelectItem>
                    {entities.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.entity_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Select value={selectedStatus || "all"} onValueChange={(v) => setSelectedStatus(v === "all" ? "" : v)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedOutcome || "all"} onValueChange={(v) => setSelectedOutcome(v === "all" ? "" : v)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Outcomes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Outcomes</SelectItem>
                  {OUTCOME_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Assessments Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Vendor</TableHead>
                  {isDPO && <TableHead>Entity</TableHead>}
                  <TableHead>Assessment Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Assessor</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAssessments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isDPO ? 8 : 7} className="text-center py-8 text-muted-foreground">
                      No assessments found. Click "New Assessment" to create one.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAssessments.map(assessment => (
                    <TableRow 
                      key={assessment.id} 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/compliance/due-diligence/${assessment.id}`)}
                    >
                      <TableCell className="font-mono text-sm">{assessment.assessment_reference || '-'}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{assessment.vendor_name}</p>
                          <p className="text-xs text-muted-foreground">{assessment.vendor_reference}</p>
                        </div>
                      </TableCell>
                      {isDPO && <TableCell>{assessment.entity_name}</TableCell>}
                      <TableCell>{assessment.assessment_date?.split('T')[0] || '-'}</TableCell>
                      <TableCell>{getStatusBadge(assessment.status)}</TableCell>
                      <TableCell>{getOutcomeBadge(assessment.assessment_outcome)}</TableCell>
                      <TableCell>{assessment.assessor_name || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2" onClick={e => e.stopPropagation()}>
                          {!isReadOnly && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate(`/compliance/due-diligence/${assessment.id}`)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setAssessmentToDelete(assessment);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Assessment</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete assessment "{assessmentToDelete?.assessment_reference}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
